import { useState, useEffect } from 'react';
import { axios } from '../App';
import { Radar, Satellite, Shield, AlertTriangle, Plane, Waves, Droplets, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 3000);  // 3 s poll for live stats
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      // On manual click: server-side reset (clear stale L-* tracks + live buffers)
      // and disarm Live Demo + Auto Engage in this browser.
      if (manual) {
        try {
          const { data } = await axios.post('/dashboard/reset');
          const cleared =
            (data?.cleared_live_detections || 0) +
            (data?.cleared_fused_detections || 0);
          // Disarm Live Demo + Auto Engage flags so cross-page auto-arm stops
          ['bk_live_demo', 'bk_auto_engage', 'bk_demo_feed', 'bk_auto_handoff']
            .forEach((k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } });
          toast.success(
            `Dashboard refreshed · cleared ${cleared} stale tracks · Live Demo + Auto Engage disarmed`
          );
        } catch (resetErr) {
          // Non-fatal: still fall through to data reload
          toast.warning('Reset endpoint failed — reloading data only');
        }
      }
      // Live HAL-driven payload — counts go to 0 when no sensors push data.
      const { data } = await axios.get('/dashboard/live-stats');
      setStats(data);
      setDrones(data.drones || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.warn('Dashboard load failed:', error.message);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sky-500 font-mono text-sm">LOADING SYSTEM STATUS...</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Active Drones',
      value: stats?.active_drones || 0,
      icon: Radar,
      color: 'red',
      status: 'live'
    },
    {
      title: 'Air Domain',
      value: stats?.air_drones || 0,
      icon: Plane,
      color: 'sky',
      status: 'stable'
    },
    {
      title: 'Surface Domain',
      value: stats?.surface_drones || 0,
      icon: Waves,
      color: 'emerald',
      status: 'stable'
    },
    {
      title: 'Underwater',
      value: stats?.underwater_drones || 0,
      icon: Droplets,
      color: 'blue',
      status: 'stable'
    },
    {
      title: 'Critical Threats',
      value: stats?.critical_threats || 0,
      icon: AlertTriangle,
      color: 'red',
      status: 'live'
    },
    {
      title: 'Sensors Online',
      value: stats?.operational_sensors || 0,
      total: stats?.total_sensors || 0,
      icon: Satellite,
      color: 'emerald',
      status: 'stable'
    },
    {
      title: 'C-Measures',
      value: stats?.active_countermeasures || 0,
      icon: Shield,
      color: 'amber',
      status: 'stable'
    }
  ];

  const domainIcons = {
    air: Plane,
    surface: Waves,
    underwater: Droplets
  };

  const domainColors = {
    air: 'text-sky-500',
    surface: 'text-emerald-500',
    underwater: 'text-blue-500'
  };

  return (
    <div className="p-6 space-y-6" data-testid="dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase">MCI BKOS</h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Multi-Domain Counter-Drone Detection & Mitigation System
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadData(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed border border-zinc-800 hover:border-sky-500/40 text-xs font-mono uppercase tracking-wider text-zinc-300 hover:text-sky-400 rounded-sm transition-colors"
            data-testid="dashboard-refresh-button"
            title="Refresh dashboard"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {lastRefresh && (
            <span className="text-[10px] font-mono text-zinc-600 uppercase hidden md:inline" data-testid="dashboard-last-refresh">
              Updated {lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <div className={`w-2 h-2 rounded-full ${stats?.has_signal ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)] animate-pulse' : 'bg-zinc-500'}`}></div>
          <span className="text-xs font-mono uppercase" data-testid="signal-status">
            <span className={stats?.has_signal ? 'text-emerald-400' : 'text-zinc-500'}>
              {stats?.has_signal ? `LIVE · ${stats.sensors_with_signal?.length || 0} feed${(stats.sensors_with_signal?.length || 0) === 1 ? '' : 's'}` : 'NO SIGNAL'}
            </span>
          </span>
        </div>
      </div>

      {!stats?.has_signal && (
        <div className="bg-zinc-900/60 border border-amber-500/40 rounded-sm px-4 py-3 flex items-center gap-3" data-testid="no-signal-banner">
          <AlertTriangle size={18} className="text-amber-400" />
          <div className="flex-1">
            <div className="text-sm font-bold text-amber-300 uppercase tracking-tight">No sensor signal</div>
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
              Waiting for HAL contacts (last 30 s window). Active drones = 0 until at least one sensor pushes data.
            </div>
          </div>
          <div className="text-[10px] font-mono text-zinc-500 uppercase">
            {stats?.operational_sensors ?? 0}/{stats?.total_sensors ?? 0} sensors online
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="bg-zinc-950/80 border border-zinc-800 p-6 rounded-sm hover:border-zinc-700 transition-colors relative overflow-hidden group corner-accent"
              data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs font-mono tracking-wider text-zinc-500 uppercase">
                    {stat.title}
                  </p>
                  <p className={`text-3xl font-bold mt-2 text-${stat.color}-500`}>
                    {stat.value}{stat.total ? `/${stat.total}` : ''}
                  </p>
                </div>
                <Icon className={`text-${stat.color}-500`} size={24} />
              </div>
              <div className="flex items-center gap-2">
                <div
                  className={`w-2 h-2 rounded-full ${
                    stat.status === 'live'
                      ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse'
                      : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]'
                  }`}
                ></div>
                <span className="text-xs font-mono text-zinc-600 uppercase">{stat.status}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-6" data-testid="active-detections">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold tracking-tight uppercase">Active Detections</h2>
          <Radar size={20} className="text-red-500 animate-pulse" />
        </div>
        <div className="space-y-3">
          {drones.length === 0 ? (
            <p className="text-sm text-zinc-600 font-mono">No active drone detections</p>
          ) : (
            drones.map((drone) => {
              const DomainIcon = domainIcons[drone.domain] || Radar;
              const colorClass = domainColors[drone.domain] || 'text-zinc-500';
              return (
                <div
                  key={drone.id}
                  className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-sm hover:border-zinc-700 transition-colors"
                  data-testid={`drone-item-${drone.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <DomainIcon className={colorClass} size={20} />
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-zinc-100">Drone ID: {drone.drone_id}</h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className={`text-xs font-mono uppercase ${colorClass}`}>
                            {drone.domain}
                          </span>
                          {drone.drone_type && (
                            <span className="text-xs text-zinc-500">{drone.drone_type}</span>
                          )}
                          {drone.speed && (
                            <span className="text-xs text-zinc-500">{drone.speed} km/h</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`text-xs font-mono uppercase px-2 py-1 rounded-sm ${
                        drone.threat_level === 'critical'
                          ? 'bg-red-900/50 text-red-300 border border-red-800'
                          : drone.threat_level === 'high'
                          ? 'bg-amber-900/50 text-amber-300 border border-amber-800'
                          : drone.threat_level === 'medium'
                          ? 'bg-yellow-900/50 text-yellow-300 border border-yellow-800'
                          : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                      }`}
                    >
                      {drone.threat_level}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}